/**
 * Event Bus for eTrainer Application
 * 
 * This module provides a centralized event communication system for the eTrainer application.
 * It uses the 'mitt' library to create a lightweight event emitter that allows different 
 * components to communicate without tight coupling.
 * 
 * The event bus handles two main types of events:
 * 1. REQUEST events - Components request actions (e.g., navigation, state changes)
 * 2. ACTION events - Direct actions that notify other components of changes
 * 
 * Event naming convention:
 * - request* methods: Emit requests that other components should handle
 * - on*Request* methods: Listen for request events
 * - action methods: Emit direct actions/notifications
 * - on* methods: Listen for action events
 * - disable* methods: Remove event listeners
 */

import mitt from 'mitt'
import testevents from './events'

// Create the main event emitter instance using mitt library
const emitter = mitt()

// Helper function to disable (remove) event listeners
const disable = (event, handler) => emitter.off(event, handler)

export default{
        // ========================================
        // GENERIC EVENT MANAGEMENT
        // ========================================
        
        /**
         * Generic method to disable any event listener
         * @param {string} event - The event name to disable
         * @param {function} handler - The specific handler function to remove
         */
        disable(event, handler)                     { emitter.off(event, handler) },

        // ========================================
        // NAVIGATION REQUEST EVENT DISABLERS
        // These methods remove listeners for navigation request events
        // ========================================
        
        disableRequestGoToPreviousQuestion(handler) { disable(testevents.requestPreviousQuestion, handler) },
        disableRequestGoToNextQuestion(handler)     { disable(testevents.requestNextQuestion, handler) },
        disableRequestGoToPreviousBlock(handler)    { disable(testevents.requestPreviousBlock, handler) },
        disableRequestGoToNextBlock(handler)        { disable(testevents.requestNextBlock, handler) },
        disableRequestGoBack(handler)               { disable(testevents.requestGoBack, handler) },
        disableRequestGoToQuestionNumber(handler)   { disable(testevents.requestSetQuestionNumber, handler) },
        disableRequestEndTest(handler)              { disable(testevents.requestEndTest, handler) },
        disableRequestMarkQuestion(handler)         { disable(testevents.requestMarkQuestion, handler) },
        disableRequestUnmarkQuestion(handler)       { disable(testevents.requestUnmarkQuestion, handler) },
        disableRequestGoToBookmarks(handler)        { disable(testevents.requestGoToBookmarks, handler) },
        disableRequestSubCategoryTimedOut(handler)  { disable(testevents.requestSubCategoryTimedOut, handler); },
        disableRequestGoToPreviousBookmark(handler) { disable(testevents.requestGoToPreviousBookmark, handler) },
        disableRequestGoToNextBookmark(handler)     { disable(testevents.requestGoToNextBookmark, handler) },

        // ========================================
        // NAVIGATION ACTION EVENT DISABLERS
        // These methods remove listeners for direct navigation action events
        // ========================================
        
        disableGoToPreviousQuestion(handler)        { disable(testevents.previousQuestion, handler) },
        disableGoToNextQuestion(handler)            { disable(testevents.nextQuestion, handler) },
        disableGoToPreviousBlock(handler)           { disable(testevents.previousBlock, handler) },
        disableGoToNextBlock(handler)               { disable(testevents.nextBlock, handler) },
        disableGoBack(handler)                      { disable(testevents.goBack, handler) },
        disableGoToQuestionNumber(handler)          { disable(testevents.setQuestionNumber, handler) },
        disableMarkQuestion(handler)                { disable(testevents.markQuestion, handler) },
        disableUnmarkQuestion(handler)              { disable(testevents.unmarkQuestion, handler) },
        disableSubCategoryTimedOut(handler)         { disable(testevents.subCategoryTimedOut, handler); },
        disableTestTimedOut(handler)                { disable(testevents.testTimedOut, handler) },

        // ========================================
        // TEST LIFECYCLE EVENT DISABLERS
        // These methods remove listeners for test state management events
        // ========================================
        
        disableCancelEndModal(handler)              { disable(testevents.cancelEndModal, handler) },
        disableStartTest(handler)                   { disable(testevents.startTest, handler) },
        disableIgnoringQuestion(handler)            { disable(testevents.ignoreQuestion, handler) },
        disableSelectAnswer(handler)                { disable(testevents.selectAnswer, handler) },
        disableChangeMainCategory(handler)          { disable(testevents.mainCategoryChanged, handler) },
        disableChangeSubCategory(handler)           { disable(testevents.subCategoryChanged, handler) },
        disableChangeBlock(handler)                 { disable(testevents.blockChanged, handler) },
        disableEndTest(handler)                     { disable(testevents.endTest, handler) },
        disableEndSelfRating(handler)               { disable(testevents.endSelfRating, handler) },
        disableQuitTest(handler)                    { disable(testevents.quitTest, handler) },
        disableSessionEnded(handler)                { disable(testevents.sessionEnded, handler) },
        disablePostInsertMemorizationInterstitialVisibilityChanged(handler) {
            disable(testevents.postInsertMemorizationInterstitialVisibilityChanged, handler)
        },
        disableRequestContinuePostInsertMemorizationInterstitial(handler) {
            disable(testevents.requestContinuePostInsertMemorizationInterstitial, handler)
        },


        // ========================================
        // SESSION MANAGEMENT
        // Events related to user session lifecycle
        // ========================================

        /**
         * Emit session ended event - notifies all components that the user session has ended
         */
        sessionEnded()                              { emitter.emit(testevents.sessionEnded) },
        /**
         * Listen for session ended events
         * @param {function} handler - Callback function to execute when session ends
         */
        onSessionEnded(handler)                     { emitter.on(testevents.sessionEnded, handler) },

        // ========================================
        // NAVIGATION REQUEST EVENTS
        // These events are requests for navigation actions that need to be handled by navigation controllers
        // ========================================

        /**
         * Request to navigate to the previous question in the test
         */
        requestGoToPreviousQuestion()               { emitter.emit(testevents.requestPreviousQuestion) },
        /**
         * Listen for requests to go to previous question
         * @param {function} handler - Function to handle the navigation request
         */
        onRequestingGoToPreviousQuestion(handler)   { emitter.on(testevents.requestPreviousQuestion, handler) },

        /**
         * Request to navigate to the next question in the test
         */
        requestGoToNextQuestion()                   { emitter.emit(testevents.requestNextQuestion) },
        /**
         * Listen for requests to go to next question
         * @param {function} handler - Function to handle the navigation request
         */
        onRequestingGoToNextQuestion(handler)       { emitter.on(testevents.requestNextQuestion, handler) },

        /**
         * Request to navigate to the previous block of questions
         */
        requestGoToPreviousBlock()                  { emitter.emit(testevents.requestPreviousBlock) },
        /**
         * Listen for requests to go to previous block
         * @param {function} handler - Function to handle the block navigation request
         */
        onRequestingGoToPreviousBlock(handler)      { emitter.on(testevents.requestPreviousBlock, handler) },

        /**
         * Request to navigate to the next block of questions
         */
        requestGoToNextBlock()                      { emitter.emit(testevents.requestNextBlock) },
        /**
         * Listen for requests to go to next block
         * @param {function} handler - Function to handle the block navigation request
         */
        onRequestingGoToNextBlock(handler)          { emitter.on(testevents.requestNextBlock, handler) },

        /**
         * Request to go back to previous screen/state
         */
        requestGoBack()                             { emitter.emit(testevents.requestGoBack) },
        /**
         * Listen for requests to go back
         * @param {function} handler - Function to handle the go back request
         */
        onRequestingGoBack(handler)                 { emitter.on(testevents.requestGoBack, handler) },

        /**
         * Request to navigate to a specific question number
         * @param {number} number - The question number to navigate to
         */
        requestGoToQuestionNumber(number)           { emitter.emit(testevents.requestSetQuestionNumber, number) },
        /**
         * Listen for requests to go to a specific question number
         * @param {function} handler - Function to handle the navigation request (receives question number)
         */
        onRequestingGoToQuestionNumber(handler)     { emitter.on(testevents.requestSetQuestionNumber, handler) },

        /**
         * Request to end the current test
         */
        requestEndTest()                            { emitter.emit(testevents.requestEndTest) },
        /**
         * Listen for requests to end the test
         * @param {function} handler - Function to handle the end test request
         */
        onRequestingEndTest(handler)                { emitter.on(testevents.requestEndTest, handler) },

        /**
         * Request to mark/bookmark a question for later review
         * @param {object} data - Question data to be marked
         */
        requestMarkQuestion(data)                   { emitter.emit(testevents.requestMarkQuestion, data) },
        /**
         * Listen for requests to mark questions
         * @param {function} handler - Function to handle the mark request (receives question data)
         */
        onRequestingMarkQuestion(handler)           { emitter.on(testevents.requestMarkQuestion, handler) },

        /**
         * Request to unmark/remove bookmark from a question
         * @param {object} data - Question data to be unmarked
         */
        requestUnmarkQuestion(data)                 { emitter.emit(testevents.requestUnmarkQuestion, data) },
        /**
         * Listen for requests to unmark questions
         * @param {function} handler - Function to handle the unmark request (receives question data)
         */
        onRequestingUnmarkQuestion(handler)         { emitter.on(testevents.requestUnmarkQuestion, handler) },

        /**
         * Request to navigate to bookmarked questions view
         */
        requestGoToBookmarks()                      { emitter.emit(testevents.requestGoToBookmarks) },
        /**
         * Listen for requests to view bookmarks
         * @param {function} handler - Function to handle the bookmarks navigation request
         */
        onRequestingGoToBookmarks(handler)          { emitter.on(testevents.requestGoToBookmarks, handler) },

        /**
         * Request to handle subcategory timeout scenario
         */
        requestSubCategoryTimedOut()                { emitter.emit(testevents.requestSubCategoryTimedOut); },
        /**
         * Listen for subcategory timeout requests
         * @param {function} handler - Function to handle subcategory timeout
         */
        onRequestingSubCategoryTimedOut(handler)    { emitter.on(testevents.requestSubCategoryTimedOut, handler); },

        /**
         * Request to navigate to previous bookmark in bookmark revision mode
         */
        requestGoToPreviousBookmark()               { emitter.emit(testevents.requestGoToPreviousBookmark) },
        /**
         * Listen for requests to go to previous bookmark
         * @param {function} handler - Function to handle the previous bookmark request
         */
        onRequestingGoToPreviousBookmark(handler)   { emitter.on(testevents.requestGoToPreviousBookmark, handler) },

        /**
         * Request to navigate to next bookmark in bookmark revision mode
         */
        requestGoToNextBookmark()                   { emitter.emit(testevents.requestGoToNextBookmark) },
        /**
         * Listen for requests to go to next bookmark
         * @param {function} handler - Function to handle the next bookmark request
         */
        onRequestingGoToNextBookmark(handler)       { emitter.on(testevents.requestGoToNextBookmark, handler) },
        requestContinuePostInsertMemorizationInterstitial() {
            emitter.emit(testevents.requestContinuePostInsertMemorizationInterstitial)
        },
        onRequestingContinuePostInsertMemorizationInterstitial(handler) {
            emitter.on(testevents.requestContinuePostInsertMemorizationInterstitial, handler)
        },

        // ========================================
        // DIRECT NAVIGATION ACTION EVENTS
        // These events represent actual navigation actions being performed
        // ========================================

        /**
         * Perform navigation to previous question (actual action, not request)
         */
        goToPreviousQuestion()                      { emitter.emit(testevents.previousQuestion) },
        /**
         * Listen for previous question navigation actions
         * @param {function} handler - Function to execute when navigation occurs
         */
        onGoToPreviousQuestion(handler)             { emitter.on(testevents.previousQuestion, handler) },

        /**
         * Perform navigation to next question (actual action, not request)
         */
        goToNextQuestion()                          { emitter.emit(testevents.nextQuestion) },
        /**
         * Listen for next question navigation actions
         * @param {function} handler - Function to execute when navigation occurs
         */
        onGoToNextQuestion(handler)                 { emitter.on(testevents.nextQuestion, handler) },

        /**
         * Perform navigation to previous block (actual action, not request)
         */
        goToPreviousBlock()                         { emitter.emit(testevents.previousBlock) },
        /**
         * Listen for previous block navigation actions
         * @param {function} handler - Function to execute when navigation occurs
         */
        onGoToPreviousBlock(handler)                { emitter.on(testevents.previousBlock, handler) },

        /**
         * Perform navigation to next block (actual action, not request)
         */
        goToNextBlock()                             { emitter.emit(testevents.nextBlock) },
        /**
         * Listen for next block navigation actions
         * @param {function} handler - Function to execute when navigation occurs
         */
        onGoToNextBlock(handler)                    { emitter.on(testevents.nextBlock, handler) },

        /**
         * Perform go back navigation action
         */
        goBack()                                    { emitter.emit(testevents.goBack) },
        /**
         * Listen for go back navigation actions
         * @param {function} handler - Function to execute when go back occurs
         */
        onGoBack(handler)                           { emitter.on(testevents.goBack, handler) },

        /**
         * Navigate to a specific question number (actual action)
         * @param {number} number - The question number to navigate to
         */
        goToQuestionNumber(number)                  { emitter.emit(testevents.setQuestionNumber, number) },
        /**
         * Listen for question number navigation actions
         * @param {function} handler - Function to execute when navigation occurs (receives question number)
         */
        onGoToQuestionNumber(handler)               { emitter.on(testevents.setQuestionNumber, handler) },

        /**
         * Mark/bookmark a question (actual action)
         * @param {string|number} id - The ID of the question to mark
         */
        markQuestion(id)                            { emitter.emit(testevents.markQuestion, id) },
        /**
         * Listen for question marking actions
         * @param {function} handler - Function to execute when question is marked (receives question ID)
         */
        onMarkingQuestion(handler)                  { emitter.on(testevents.markQuestion, handler) },

        /**
         * Unmark/remove bookmark from a question (actual action)
         * @param {string|number} id - The ID of the question to unmark
         */
        unmarkQuestion(id)                          { emitter.emit(testevents.unmarkQuestion, id) },
        /**
         * Listen for question unmarking actions
         * @param {function} handler - Function to execute when question is unmarked (receives question ID)
         */
        onUnmarkingQuestion(handler)                { emitter.on(testevents.unmarkQuestion, handler) },

        // ========================================
        // TEST LIFECYCLE EVENTS
        // Events that control the test state and user interactions
        // ========================================

        /**
         * Ignore/skip the current question
         */
        ignoreQuestion()            { emitter.emit(testevents.ignoreQuestion); },
        /**
         * Listen for question ignore actions
         * @param {function} handler - Function to execute when question is ignored
         */
        onIgnoringQuestion(handler) { emitter.on(testevents.ignoreQuestion, handler); },

        /**
         * Start a test
         * @param {number|string} idx - Test index or identifier
         */
        startTest(idx)          { emitter.emit(testevents.startTest, idx); },
        /**
         * Listen for test start events
         * @param {function} handler - Function to execute when test starts (receives test index)
         */
        onStartingTest(handler) { emitter.on(testevents.startTest, handler); },

        /**
         * End the current test
         */
        endTest()               { emitter.emit(testevents.endTest); },
        /**
         * Listen for test end events
         * @param {function} handler - Function to execute when test ends
         */
        onEndingTest(handler)   { emitter.on(testevents.endTest, handler); },

        /**
         * End the self-rating phase of the test
         */
        endSelfRating()               { emitter.emit(testevents.endSelfRating); },
        /**
         * Listen for self-rating end events
         * @param {function} handler - Function to execute when self-rating ends
         */
        onEndingSelfRating(handler)   { emitter.on(testevents.endSelfRating, handler); },

        /**
         * Quit the current test (different from ending - implies early termination)
         */
        quitTest()              { emitter.emit(testevents.quitTest); },
        /**
         * Listen for test quit events
         * @param {function} handler - Function to execute when test is quit
         */
        onQuittingTest(handler) { emitter.on(testevents.quitTest, handler); },

        postInsertMemorizationInterstitialVisibilityChanged(isVisible) {
            emitter.emit(testevents.postInsertMemorizationInterstitialVisibilityChanged, isVisible)
        },
        onPostInsertMemorizationInterstitialVisibilityChanged(handler) {
            emitter.on(testevents.postInsertMemorizationInterstitialVisibilityChanged, handler)
        },

        // ========================================
        // TIMEOUT EVENTS
        // Events related to time limits and timeouts
        // ========================================

        /**
         * Notify that a subcategory has timed out
         */
        subCategoryTimedOut()          { emitter.emit(testevents.subCategoryTimedOut); },
        /**
         * Listen for subcategory timeout events
         * @param {function} handler - Function to execute when subcategory times out
         */
        onSubCategoryTimedOut(handler) { emitter.on(testevents.subCategoryTimedOut, handler); },

        /**
         * Notify that the entire test has timed out
         */
        testTimedOut()          { emitter.emit(testevents.testTimedOut); },
        /**
         * Listen for test timeout events
         * @param {function} handler - Function to execute when test times out
         */
        onTestTimedOut(handler) { emitter.on(testevents.testTimedOut, handler); },

        // ========================================
        // CONFIGURATION CHANGE EVENTS
        // Events that notify about changes in test configuration or categories
        // ========================================

        /**
         * Notify about test mode change (e.g., practice mode, exam mode)
         * @param {string} newMode - The new test mode
         */
        changeTestMode(newMode)     { emitter.emit(testevents.testModeChanged, newMode); },
        /**
         * Listen for test mode changes
         * @param {function} handler - Function to execute when test mode changes (receives new mode)
         */
        onTestModeChanged(handler)  { emitter.on(testevents.testModeChanged, handler); },

        /**
         * Notify about block change in the test
         * @param {object} block - The new block data
         */
        changeBlock(block)          { emitter.emit(testevents.blockChanged, block) },
        /**
         * Listen for block changes
         * @param {function} handler - Function to execute when block changes (receives block data)
         */
        onChangingBlock(handler)    { emitter.on(testevents.blockChanged, handler); },

        /**
         * Notify about subcategory change
         * @param {object} subCategory - The new subcategory data
         * @param {boolean} isDefault - Whether this is a default subcategory selection
         */
        changeSubCategory(subCategory, isDefault=false) {
            emitter.emit(testevents.subCategoryChanged, subCategory, isDefault);
        },
        /**
         * Listen for subcategory changes
         * @param {function} handler - Function to execute when subcategory changes (receives subcategory and isDefault flag)
         */
        onChangingSubCategory(handler) { emitter.on(testevents.subCategoryChanged, handler); },

        /**
         * Notify about main category change
         * @param {object} mainCategory - The new main category data
         */
        changeMainCategory(mainCategory)    { emitter.emit(testevents.mainCategoryChanged, mainCategory); },
        /**
         * Listen for main category changes
         * @param {function} handler - Function to execute when main category changes (receives category data)
         */
        onChangingMainCategory(handler)     { emitter.on(testevents.mainCategoryChanged, handler); },

        // ========================================
        // CONTENT LOADING EVENTS
        // Events related to loading additional content or questions
        // ========================================

        /**
         * Request to load more exercise questions (for infinite scroll or pagination)
         */
        loadMoreExerciseQuestions()            { emitter.emit(testevents.loadMoreExerciseQuestions); },
        /**
         * Listen for load more questions events
         * @param {function} handler - Function to execute when more questions need to be loaded
         */
        onLoadMoreExerciseQuestions(handler)   { emitter.on(testevents.loadMoreExerciseQuestions, handler); },

        // ========================================
        // ANSWER SELECTION EVENTS
        // Events related to user interactions with questions and answers
        // ========================================

        /**
         * Notify about answer selection
         * @param {object} answerDetails - Details about the selected answer (question ID, answer ID, etc.)
         */
        selectAnswer(answerDetails) { 
            emitter.emit(testevents.selectAnswer, answerDetails) },
        /**
         * Listen for answer selection events
         * @param {function} handler - Function to execute when answer is selected (receives answer details)
         */
        onSelectingAnswer(handler)  { 
            emitter.on(testevents.selectAnswer, handler) },

        // ========================================
        // TIMER CONTROL EVENTS
        // Events for controlling test timers (pause/resume functionality)
        // ========================================

        /**
         * Pause the test timer
         */
        pauseTestTimer()            { emitter.emit(testevents.pauseTestTimer) },
        /**
         * Listen for timer pause events
         * @param {function} handler - Function to execute when timer is paused
         */
        onPausingTestTimer(handler) { emitter.on(testevents.pauseTestTimer, handler) },

        /**
         * Resume the test timer
         */
        resumeTestTimer()           { emitter.emit(testevents.resumeTestTimer) },
        /**
         * Listen for timer resume events
         * @param {function} handler - Function to execute when timer is resumed
         */
        onResumingTestTimer(handler){ emitter.on(testevents.resumeTestTimer, handler) },

        // ========================================
        // MODAL CONTROL EVENTS
        // Events for controlling modal dialogs and popups
        // ========================================

        /**
         * Cancel/close the end test modal dialog
         */
        cancelEndModal()           {  emitter.emit(testevents.cancelEndModal) },
        /**
         * Listen for end modal cancellation events
         * @param {function} handler - Function to execute when end modal is cancelled
         */
        onCancellingEndModal(handler){ emitter.on(testevents.cancelEndModal, handler) },
    }

